import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  Tag,
  Modal,
  Form,
  Input,
  message,
  Tabs,
  Typography,
  Avatar,
  Tooltip,
  Empty,
  Badge,
} from 'antd';
import {
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import type { ColumnsType } from 'antd/es/table';
import {
  attendanceService,
  type Attendance,
  type CreateManualAttendanceRequest,
  type UpdateAttendanceRequest,
  type GetAttendanceHistoryParams,
} from '../services/attendanceService';
import { employeeService } from '../services/employeeService';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const AttendanceManagementPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [modal, contextHolder] = Modal.useModal();

  // ==================== STATE ====================
  const [activeTab, setActiveTab] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [historyParams, setHistoryParams] = useState<GetAttendanceHistoryParams>({
    page: 1,
    limit: 10,
  });

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Attendance | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<Attendance | null>(null);

  const [form] = Form.useForm();

  // ==================== QUERIES ====================

  // ດຶງລາຍງານປະຈຳວັນ
  const { data: dailyReport, isLoading: dailyLoading } = useQuery(
    ['dailyReport', selectedDate],
    () =>
      attendanceService.getDailyReport({
        date: selectedDate.toISOString().split('T')[0],
      }),
    {
      enabled: activeTab === 'daily',
    }
  );

  // ດຶງປະຫວັດທັງໝົດ
  const { data: historyData, isLoading: historyLoading } = useQuery(
    ['attendanceHistory', historyParams],
    () => attendanceService.getAttendanceHistory(historyParams),
    {
      enabled: activeTab === 'history',
      keepPreviousData: true,
    }
  );

  // ດຶງລາຍຊື່ພະນັກງານ
  const { data: employeesData } = useQuery('employees', () =>
    employeeService.getEmployees({ limit: 100, status: 'active' })
  );

  // ==================== MUTATIONS ====================

  // ສ້າງບັນທຶກແບບ Manual
  const createMutation = useMutation(attendanceService.createManualAttendance, {
    onSuccess: () => {
      message.success('ສ້າງບັນທຶກສຳເລັດ');
      setIsModalVisible(false);
      form.resetFields();
      queryClient.invalidateQueries(['dailyReport']);
      queryClient.invalidateQueries(['attendanceHistory']);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    },
  });

  // ແກ້ໄຂບັນທຶກ
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: UpdateAttendanceRequest }) =>
      attendanceService.updateAttendance(id, data),
    {
      onSuccess: () => {
        message.success('ແກ້ໄຂຂໍ້ມູນສຳເລັດ');
        setIsModalVisible(false);
        setEditingRecord(null);
        form.resetFields();
        queryClient.invalidateQueries(['dailyReport']);
        queryClient.invalidateQueries(['attendanceHistory']);
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
      },
    }
  );

  // ລຶບບັນທຶກ
  const deleteMutation = useMutation(attendanceService.deleteAttendance, {
    onSuccess: () => {
      message.success('ລຶບຂໍ້ມູນສຳເລັດ');
      queryClient.invalidateQueries(['dailyReport']);
      queryClient.invalidateQueries(['attendanceHistory']);
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'ເກີດຂໍ້ຜິດພາດ');
    },
  });

  // ==================== HANDLERS ====================

  const handleCreate = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      date: dayjs(selectedDate),
      status: 'present',
    });
    setIsModalVisible(true);
  };

  const handleEdit = (record: Attendance) => {
    setEditingRecord(record);
    form.setFieldsValue({
      userId: record.userId._id,
      date: dayjs(record.date),
      checkInTime: dayjs(record.checkInTime),
      checkOutTime: record.checkOutTime ? dayjs(record.checkOutTime) : null,
      status: record.status,
      notes: record.notes,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (record: Attendance) => {
    modal.confirm({
      title: 'ຢືນຢັນການລຶບ',
      content: (
        <div>
          <p>
            ຕ້ອງການລຶບບັນທຶກຂອງ <strong>{record.userId.name}</strong> ວັນທີ່{' '}
            <strong>{dayjs(record.date).format('DD/MM/YYYY')}</strong> ຫຼື ບໍ່?
          </p>
        </div>
      ),
      okText: 'ລຶບ',
      okType: 'danger',
      cancelText: 'ຍົກເລີກ',
      onOk: () => deleteMutation.mutate(record._id),
    });
  };

  const handleViewDetail = (record: Attendance) => {
    setSelectedRecord(record);
    setDetailModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingRecord) {
        // ແກ້ໄຂ
        const data: UpdateAttendanceRequest = {
          checkInTime: values.checkInTime?.toISOString(),
          checkOutTime: values.checkOutTime?.toISOString(),
          status: values.status,
          notes: values.notes,
        };
        updateMutation.mutate({ id: editingRecord._id, data });
      } else {
        // ສ້າງໃໝ່
        const data: CreateManualAttendanceRequest = {
          userId: values.userId,
          date: values.date.format('YYYY-MM-DD'),
          checkInTime: values.checkInTime.toISOString(),
          checkOutTime: values.checkOutTime?.toISOString(),
          status: values.status,
          notes: values.notes,
        };
        createMutation.mutate(data);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

//   const getStatusColor = (
//     status: string
//   ): 'success' | 'error' | 'warning' | 'processing' | 'default' => {
//     const colorMap: Record
//       string,
//       'success' | 'error' | 'warning' | 'processing' | 'default'
//     > = {
//       present: 'success',
//       late: 'warning',
//       absent: 'error',
//       'half-day': 'processing',
//       'on-leave': 'default',
//     };
//     return colorMap[status] || 'default';
//   };

  const getStatusText = (status: string) => {
    return attendanceService.formatStatus(status as Attendance['status']);
  };

  // ==================== TABLE COLUMNS ====================

  const dailyColumns: ColumnsType<Attendance> = [
    {
      title: 'ພະນັກງານ',
      key: 'employee',
      width: 200,
      render: (_, record) => (
        <Space>
          <Avatar
            style={{ backgroundColor: '#1890ff' }}
            icon={<UserOutlined />}
          />
          <div>
            <div style={{ fontWeight: 500 }}>{record.userId.name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.userId.employeeId}
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'ຕຳແໜ່ງ',
      dataIndex: ['userId', 'role'],
      key: 'role',
      width: 100,
      render: (role: string) => {
        const roleMap: Record<string, { text: string; color: string }> = {
          admin: { text: 'Admin', color: 'red' },
          cashier: { text: 'Cashier', color: 'blue' },
          therapist: { text: 'Therapist', color: 'green' },
        };
        const roleInfo = roleMap[role] || { text: role, color: 'default' };
        return <Tag color={roleInfo.color}>{roleInfo.text}</Tag>;
      },
    },
    {
      title: 'ເວລາເຂົ້າ',
      dataIndex: 'checkInTime',
      key: 'checkInTime',
      width: 100,
      render: (time: string) => (
        <Space>
          <ClockCircleOutlined style={{ color: '#52c41a' }} />
          <Text>{dayjs(time).format('HH:mm')}</Text>
        </Space>
      ),
    },
    {
      title: 'ເວລາອອກ',
      dataIndex: 'checkOutTime',
      key: 'checkOutTime',
      width: 100,
      render: (time: string) =>
        time ? (
          <Space>
            <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
            <Text>{dayjs(time).format('HH:mm')}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
    {
      title: 'ຊົ່ວໂມງທຳງານ',
      dataIndex: 'actualWorkHours',
      key: 'actualWorkHours',
      width: 120,
      render: (hours: number) => (
        <Text strong>{hours.toFixed(2)} ຊົ່ວໂມງ</Text>
      ),
      sorter: (a, b) => a.actualWorkHours - b.actualWorkHours,
    },
    {
      title: 'ເວລາສາຍ',
      dataIndex: 'lateMinutes',
      key: 'lateMinutes',
      width: 100,
      render: (minutes: number) =>
        minutes > 0 ? (
          <Text type="warning">
            {attendanceService.formatLateTime(minutes)}
          </Text>
        ) : (
          <Text type="success">ບໍ່ສາຍ</Text>
        ),
      sorter: (a, b) => a.lateMinutes - b.lateMinutes,
    },
    {
      title: 'ສະຖານະ',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => (
        <Badge status={'processing'} text={getStatusText(status)} />
        // <Badge status={getStatusColor(status)} text={getStatusText(status)} />
      ),
      filters: [
        { text: 'ມາວຽກ', value: 'present' },
        { text: 'ມາສາຍ', value: 'late' },
        { text: 'ຂາດວຽກ', value: 'absent' },
        { text: 'ມາເຄິ່ງວັນ', value: 'half-day' },
        { text: 'ລາ', value: 'on-leave' },
      ],
    },
    {
      title: 'ການກະທຳ',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record)}
            />
          </Tooltip>
          <Tooltip title="ແກ້ໄຂ">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="ລຶບ">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const historyColumns: ColumnsType<Attendance> = [
    {
      title: 'ວັນທີ່',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date: string) => (
        <Space>
          <CalendarOutlined style={{ color: '#722ed1' }} />
          <Text>{dayjs(date).format('DD/MM/YYYY')}</Text>
        </Space>
      ),
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix(),
    },
    ...dailyColumns,
  ];

  const employees = employeesData?.data?.employees || [];

  // ==================== RENDER ====================
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: 8 }} />
          ຈັດການການເຂົ້າວຽກ
        </Title>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            ສ້າງບັນທຶກ
          </Button>
        </Space>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        {/* ==================== TAB: ລາຍງານປະຈຳວັນ ==================== */}
        <TabPane
          tab={
            <span>
              <FileTextOutlined />
              ລາຍງານປະຈຳວັນ
            </span>
          }
          key="daily"
        >
          {/* Summary Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <DatePicker
                value={dayjs(selectedDate)}
                onChange={(date) => setSelectedDate(date?.toDate() || new Date())}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
                allowClear={false}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={6} md={4}>
              <Card>
                <Statistic
                  title="ທັງໝົດ"
                  value={dailyReport?.data?.summary.total || 0}
                  prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Card>
                <Statistic
                  title="ມາວຽກ"
                  value={dailyReport?.data?.summary.present || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Card>
                <Statistic
                  title="ມາສາຍ"
                  value={dailyReport?.data?.summary.late || 0}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Card>
                <Statistic
                  title="ຂາດວຽກ"
                  value={dailyReport?.data?.summary.absent || 0}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6} md={4}>
              <Card>
                <Statistic
                  title="ລາ"
                  value={dailyReport?.data?.summary.onLeave || 0}
                  prefix={<ExclamationCircleOutlined />}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>

          <Card>
            <Table
              columns={dailyColumns}
              dataSource={dailyReport?.data?.attendances || []}
              rowKey="_id"
              loading={dailyLoading}
              scroll={{ x: 1200 }}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    description="ບໍ່ມີຂໍ້ມູນ"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ),
              }}
            />
          </Card>
        </TabPane>

        {/* ==================== TAB: ປະຫວັດທັງໝົດ ==================== */}
        <TabPane
          tab={
            <span>
              <CalendarOutlined />
              ປະຫວັດທັງໝົດ
            </span>
          }
          key="history"
        >
          <Card style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="ເລືອກພະນັກງານ"
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.label ?? '')
                      .toString()
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={(userId) =>
                    setHistoryParams({ ...historyParams, userId, page: 1 })
                  }
                  options={[
                    { label: 'ທັງໝົດ', value: undefined },
                    ...employees.map((emp: any) => ({
                      label: `${emp.name} (${emp.employeeId})`,
                      value: emp._id,
                    })),
                  ]}
                />
              </Col>
              <Col xs={24} sm={12}>
                <RangePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  onChange={(dates) => {
                    if (dates) {
                      setHistoryParams({
                        ...historyParams,
                        startDate: dates[0]?.format('YYYY-MM-DD'),
                        endDate: dates[1]?.format('YYYY-MM-DD'),
                        page: 1,
                      });
                    } else {
                      setHistoryParams({
                        ...historyParams,
                        startDate: undefined,
                        endDate: undefined,
                        page: 1,
                      });
                    }
                  }}
                />
              </Col>
            </Row>
          </Card>

          <Card>
            <Table
              columns={historyColumns}
              dataSource={historyData?.data?.attendances || []}
              rowKey="_id"
              loading={historyLoading}
              scroll={{ x: 1400 }}
              pagination={{
                current: historyParams.page,
                pageSize: historyParams.limit,
                total: historyData?.data?.pagination?.total,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} ຈາກ ${total} ລາຍການ`,
                onChange: (page, pageSize) =>
                  setHistoryParams({ ...historyParams, page, limit: pageSize }),
              }}
            />
          </Card>
        </TabPane>
      </Tabs>

      {contextHolder}

      {/* ==================== MODAL: ສ້າງ/ແກ້ໄຂ ==================== */}
      <Modal
        title={
          <Space>
            {editingRecord ? <EditOutlined /> : <PlusOutlined />}
            {editingRecord ? 'ແກ້ໄຂບັນທຶກ' : 'ສ້າງບັນທຶກໃໝ່'}
          </Space>
        }
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingRecord(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="userId"
            label="ພະນັກງານ"
            rules={[{ required: true, message: 'ກະລຸນາເລືອກພະນັກງານ' }]}
          >
            <Select
              placeholder="ເລືອກພະນັກງານ"
              showSearch
              disabled={!!editingRecord}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toString()
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={employees.map((emp: any) => ({
                label: `${emp.name} (${emp.employeeId})`,
                value: emp._id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="date"
            label="ວັນທີ່"
            rules={[{ required: true, message: 'ກະລຸນາເລືອກວັນທີ່' }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              disabled={!!editingRecord}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="checkInTime"
                label="ເວລາເຂົ້າວຽກ"
                rules={[
                  { required: true, message: 'ກະລຸນາເລືອກເວລາເຂົ້າວຽກ' },
                ]}
              >
                <DatePicker
                  showTime
                  format="HH:mm"
                  style={{ width: '100%' }}
                  picker="time"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="checkOutTime" label="ເວລາເລີກວຽກ">
                <DatePicker
                  showTime
                  format="HH:mm"
                  style={{ width: '100%' }}
                  picker="time"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="status"
            label="ສະຖານະ"
            rules={[{ required: true, message: 'ກະລຸນາເລືອກສະຖານະ' }]}
          >
            <Select placeholder="ເລືອກສະຖານະ">
              <Select.Option value="present">
                <Badge status="success" text="ມາວຽກ" />
              </Select.Option>
              <Select.Option value="late">
                <Badge status="warning" text="ມາສາຍ" />
              </Select.Option>
              <Select.Option value="absent">
                <Badge status="error" text="ຂາດວຽກ" />
              </Select.Option>
              <Select.Option value="half-day">
                <Badge status="processing" text="ມາເຄິ່ງວັນ" />
              </Select.Option>
              <Select.Option value="on-leave">
                <Badge status="default" text="ລາ" />
              </Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="ໝາຍເຫດ">
            <TextArea rows={3} placeholder="ໝາຍເຫດເພີ່ມເຕີມ..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isLoading || updateMutation.isLoading}
                icon={editingRecord ? <EditOutlined /> : <PlusOutlined />}
              >
                {editingRecord ? 'ແກ້ໄຂ' : 'ສ້າງ'}
              </Button>
              <Button
                onClick={() => {
                  setIsModalVisible(false);
                  setEditingRecord(null);
                  form.resetFields();
                }}
              >
                ຍົກເລີກ
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* ==================== MODAL: ລາຍລະອຽດ ==================== */}
      <Modal
        title={
          <Space>
            <EyeOutlined />
            ລາຍລະອຽດການເຂົ້າວຽກ
          </Space>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedRecord(null);
        }}
        footer={[
          <Button
            key="edit"
            type="primary"
            icon={<EditOutlined />}
            onClick={() => {
              if (selectedRecord) {
                setDetailModalVisible(false);
                handleEdit(selectedRecord);
              }
            }}
          >
            ແກ້ໄຂ
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setDetailModalVisible(false);
              setSelectedRecord(null);
            }}
          >
            ປິດ
          </Button>,
        ]}
        width={600}
      >
        {selectedRecord && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card size="small">
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text type="secondary">ພະນັກງານ:</Text>
                      <div style={{ marginTop: 4 }}>
                        <Space>
                          <Avatar icon={<UserOutlined />} />
                          <div>
                            <div style={{ fontWeight: 500 }}>
                              {selectedRecord.userId.name}
                            </div>
                            <div style={{ fontSize: '12px', color: '#666' }}>
                              {selectedRecord.userId.employeeId}
                            </div>
                          </div>
                        </Space>
                      </div>
                    </div>
                  </Space>
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="ວັນທີ່"
                    value={dayjs(selectedRecord.date).format('DD/MM/YYYY')}
                    prefix={<CalendarOutlined />}
                  />
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="ສະຖານະ"
                    value={getStatusText(selectedRecord.status)}
                    valueStyle={{
                      color: attendanceService.getStatusColor(
                        selectedRecord.status
                      ),
                    }}
                  />
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="ເວລາເຂົ້າວຽກ"
                    value={dayjs(selectedRecord.checkInTime).format('HH:mm')}
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="ເວລາເລີກວຽກ"
                    value={
                      selectedRecord.checkOutTime
                        ? dayjs(selectedRecord.checkOutTime).format('HH:mm')
                        : '-'
                    }
                    prefix={<ClockCircleOutlined />}
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="ຊົ່ວໂມງທຳງານ"
                    value={selectedRecord.actualWorkHours.toFixed(2)}
                    suffix="ຊົ່ວໂມງ"
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>

              <Col span={12}>
                <Card size="small">
                  <Statistic
                    title="ເວລາສາຍ"
                    value={
                      selectedRecord.lateMinutes > 0
                        ? attendanceService.formatLateTime(
                            selectedRecord.lateMinutes
                          )
                        : 'ບໍ່ສາຍ'
                    }
                    valueStyle={{
                      color:
                        selectedRecord.lateMinutes > 0 ? '#faad14' : '#52c41a',
                    }}
                  />
                </Card>
              </Col>

              {selectedRecord.notes && (
                <Col span={24}>
                  <Card size="small" title="ໝາຍເຫດ">
                    <Text>{selectedRecord.notes}</Text>
                  </Card>
                </Col>
              )}

              <Col span={24}>
                <Card size="small">
                  <Space direction="vertical" size={4}>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ສ້າງເມື່ອ:{' '}
                      {dayjs(selectedRecord.createdAt).format(
                        'DD/MM/YYYY HH:mm'
                      )}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      ອັບເດດລ່າສຸດ:{' '}
                      {dayjs(selectedRecord.updatedAt).format(
                        'DD/MM/YYYY HH:mm'
                      )}
                    </Text>
                  </Space>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AttendanceManagementPage;